import type React from "react";

import { PaginationBar } from "@/components/pagination-bar";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

/**
 * The platform-admin table primitives (POS2-1057, moved here by POS2-1138 CP2).
 *
 * A **pure move** out of `pages/admin/labelling.tsx`: no rename, no restyle, no behaviour change.
 * `/admin/customer-health` and `/admin/activity` are the second and third consumers, which is where
 * copy-pasting a table would become a liability — but a 1,200-line shipped page is not a
 * refactoring target inside another ticket's work, so the boundary is tight. `Kpi`, `Section`,
 * `Tag`, `Progress`, `Split`, the formatters and `useAdminQuery` deliberately stayed behind.
 *
 * The two things that came across are correctness rather than styling, and both have a bug behind
 * them: `Table`'s positional `renderRow(row, columns)` contract, which is what stops a cell
 * misaligning from its header (POS2-1086), and `Pagination`'s re-padding of a bar that pulls itself
 * flush.
 */

/**
 * A column and how it aligns. `numeric` right-aligns the header *and* every cell under it from
 * this one declaration, which is the whole point: the header used to derive its alignment from its
 * index while each cell decided for itself, so the two disagreed in three of the four tables
 * (Sites' "Events labelled", the shift log's "Clock in", and three Exceptions columns). One
 * property, one source. `srLabel` names an action column that shows no header text.
 */
export type Column = {
  label: string;
  numeric?: boolean;
  srLabel?: string;
  /**
   * Rendered in the header cell instead of `label` — a sort control, and nothing more ambitious
   * than that (POS2-1138 CP4). `label` stays required and keeps doing both of its other jobs: it
   * keys the cell, and a header without one would have nothing stable to key on.
   */
  header?: React.ReactNode;
  /**
   * The sort state, announced on the `<th>` where ARIA puts it rather than on whatever control the
   * header happens to contain. A button cannot carry `aria-sort`, so a sortable header that owns
   * its own semantics would silently announce nothing.
   */
  ariaSort?: "ascending" | "descending" | "none";
};

/** The alignment classes a header and its cells must share. */
export const alignment = (column: Column) => (column.numeric ? "text-right" : "text-left");

export function Table<T>({
  columns,
  rows,
  renderRow,
  isLoading,
  empty,
  footer,
}: {
  columns: Column[];
  rows: T[];
  renderRow: (row: T, columns: Column[]) => React.ReactNode;
  isLoading: boolean;
  empty: string;
  footer?: React.ReactNode;
}) {
  return (
    // The scroll container is inside the card rather than being the card, so a footer
    // stays put while a wide table scrolls under it.
    <div className="rounded-lg border border-black/[0.06] bg-white">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[960px] border-collapse text-sm">
          <thead>
            <tr className="border-b border-black/[0.06]">
              {columns.map((column) => (
                <th
                  key={column.label || column.srLabel}
                  aria-sort={column.ariaSort}
                  className={cn(
                    "px-4 py-2 text-xs font-medium uppercase tracking-wide text-gray-400",
                    alignment(column),
                  )}
                >
                  {column.header ?? column.label}
                  {column.srLabel && <span className="sr-only">{column.srLabel}</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 3 }, (_, index) => (
                <tr key={index} className="border-b border-black/[0.06] last:border-b-0">
                  <td colSpan={columns.length} className="px-4 py-3">
                    <Skeleton className="h-4 w-full" />
                  </td>
                </tr>
              ))
            ) : rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-4 py-10 text-center text-sm text-gray-500"
                >
                  {empty}
                </td>
              </tr>
            ) : (
              rows.map((row) => renderRow(row, columns))
            )}
          </tbody>
        </table>
      </div>
      {footer}
    </div>
  );
}

/**
 * `PaginationBar` pulls itself flush with `-mx-6 -mb-6`, which assumes a parent with `p-6`
 * (every other caller sits in a padded `CardContent`). These table cards have no padding,
 * so the wrapper supplies exactly the padding the bar cancels.
 */
export function Pagination(props: React.ComponentProps<typeof PaginationBar>) {
  return (
    <div className="px-6 pb-6">
      <PaginationBar {...props} />
    </div>
  );
}

/**
 * A cell that takes its alignment from its column rather than from an argument at the call
 * site — a `<Td>` cannot be aligned differently from the header above it.
 */
export function Td({
  children,
  column,
  className,
}: {
  children: React.ReactNode;
  column: Column;
  className?: string;
}) {
  return (
    <td
      className={cn(
        "px-4 py-3 text-gray-700",
        alignment(column),
        column.numeric && "tabular-nums",
        className,
      )}
    >
      {children}
    </td>
  );
}
